import React, { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { useTrace } from "./hooks/useTrace";
import { ThemeProvider } from "./contexts/ThemeContext";
import MapScene from "./components/MapScene";
import CyberNav from "./components/CyberNav";
import HopPanel from "./components/HopPanel";
import LeadModal from "./components/LeadModal";
import TraceHistory from "./components/TraceHistory";
import ThemeToggle from "./components/ThemeToggle";
import SignalWavesIcon from "./components/SignalWavesIcon";
import Stats from "./components/Stats";
import { Globe, Zap, Shield, Terminal, RotateCcw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { traceState, hops, allHops, visibleHops, traceInfo, traceSummary, error, startTrace, retry: retryTrace } = useTrace();

  const [userGeo, setUserGeo] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [currentTarget, setCurrentTarget] = useState('whatismyroute.com');
  const [traceStarted, setTraceStarted] = useState(false);
  const [showTimeoutHops, setShowTimeoutHops] = useState(false);

  // Detect user IP and auto-start trace
  useEffect(() => {
    let cancelled = false;
    let initTimer = null;

    const init = async () => {
      try {
        const res = await axios.get(`${API}/whoami`);
        if (cancelled) return;
        setUserGeo(res.data);

        if (!traceStarted) {
          setTraceStarted(true);
          initTimer = setTimeout(() => {
            if (!cancelled) startTrace('whatismyroute.com', res.data.ip);
          }, 300);
        }
      } catch (e) {
        if (cancelled) return;
        if (!traceStarted) {
          setTraceStarted(true);
          initTimer = setTimeout(() => {
            if (!cancelled) startTrace('whatismyroute.com', '');
          }, 300);
        }
      }
    };
    init();

    return () => { cancelled = true; if (initTimer) clearTimeout(initTimer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save trace to history when complete
  useEffect(() => {
    if (traceState === 'complete' && traceSummary && traceInfo) {
      axios.post(`${API}/traces`, {
        source_ip: traceInfo.source_ip,
        source_city: traceInfo.source_city,
        source_country: traceInfo.source_country,
        target: currentTarget,
        target_ip: traceInfo.target_ip,
        target_city: traceInfo.target_city,
        target_country: traceInfo.target_country,
        total_hops: traceSummary.total_hops,
        avg_latency_ms: traceSummary.avg_latency_ms,
        hops: traceSummary.hops || [],
      }).catch(() => {});

      axios.post(`${API}/analytics/event`, {
        type: 'trace_complete',
        target: currentTarget,
        hops: traceSummary.total_hops,
      }).catch(() => {});
    }
  }, [traceState, traceSummary, traceInfo, currentTarget]);

  const handleSearch = useCallback((target) => {
    setCurrentTarget(target);
    setShowTimeoutHops(false); // Reset toggle to default (hidden)
    startTrace(target, userGeo?.ip || '');
  }, [startTrace, userGeo]);

  const handleReplayTrace = useCallback((target) => {
    handleSearch(target);
  }, [handleSearch]);

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 transition-colors noise-overlay">
      <CyberNav
        onSearch={handleSearch}
        traceState={traceState}
        onOpenHistory={() => setShowHistory(true)}
      />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="relative">
            {/* Background glows */}
            <div className="absolute -top-20 -left-20 w-96 h-96 bg-cyan-500/5 dark:bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -top-10 -right-10 w-72 h-72 bg-emerald-500/3 dark:bg-emerald-500/3 rounded-full blur-3xl pointer-events-none" />

            {/* Title */}
            <div className="text-center mb-6 relative z-10">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900 dark:text-white mb-3">
                Trace your route.{" "}
                <span className="text-cyan-600 dark:text-[#00f0ff]">See every hop.</span>
              </h1>
              <p className="text-sm md:text-base text-zinc-600 dark:text-white/60 max-w-2xl mx-auto">
                Real traceroute from your IP to{" "}
                <span className="text-emerald-600 dark:text-[#00ff9d] font-mono">{currentTarget}</span>
                {" "}&mdash; live on an interactive map with real geolocation data.
              </p>

              {/* User info HUD */}
              {userGeo && (
                <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 dark:bg-white/5 border border-zinc-300 dark:border-white/10 rounded-lg">
                    <Terminal className="w-3 h-3 text-cyan-600 dark:text-[#00f0ff]" />
                    <span className="text-xs font-mono text-zinc-700 dark:text-white/70">{userGeo.ip}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 dark:bg-white/5 border border-zinc-300 dark:border-white/10 rounded-lg">
                    <SignalWavesIcon className="w-3 h-3 text-emerald-600 dark:text-[#00ff9d]" animated={false} />
                    <span className="text-xs text-zinc-700 dark:text-white/70">{userGeo.city}, {userGeo.country}</span>
                  </div>
                  {userGeo.isp && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 dark:bg-white/5 border border-zinc-300 dark:border-white/10 rounded-lg">
                      <Shield className="w-3 h-3 text-purple-600 dark:text-purple-500" />
                      <span className="text-xs text-zinc-700 dark:text-white/70">{userGeo.isp}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Main grid: Map + Hop Panel */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10">
              {/* Map */}
              <div className="lg:col-span-7">
                <div className="relative rounded-2xl overflow-hidden bg-zinc-100 dark:bg-zinc-900/50 border border-zinc-300 dark:border-white/10 shadow-2xl" 
                  style={{ minHeight: '500px' }}
                >
                  <MapScene
                    hops={showTimeoutHops ? allHops : visibleHops}
                    allHops={allHops}
                    visibleHops={visibleHops}
                    traceInfo={traceInfo}
                    traceState={traceState}
                    showTimeoutHops={showTimeoutHops}
                  />

                  {/* Map HUD */}
                  <div className="hud-overlay absolute top-3 left-3 z-[1000]">
                    <div className="px-2.5 py-1.5 rounded-lg bg-white/90 dark:bg-black/60 backdrop-blur-md border border-zinc-300 dark:border-white/10">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${
                          traceState === 'tracing' ? 'animate-pulse' : ''
                        }`} style={{
                          backgroundColor: 
                            traceState === 'tracing' ? '#00ff9d' :
                            traceState === 'complete' ? '#00f0ff' :
                            error ? '#ff3b3b' : 'rgba(255,255,255,0.3)'
                        }} />
                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-700 dark:text-white/70">
                          {traceState === 'idle' ? 'Ready' :
                           traceState === 'connecting' ? 'Connecting...' :
                           traceState === 'tracing' ? `Tracing... ${(showTimeoutHops ? allHops : visibleHops).length} hops` :
                           traceState === 'complete' ? `Complete - ${visibleHops.length} hops found` :
                           'Error'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Trace progress */}
                  {traceState === 'tracing' && (
                    <div className="absolute bottom-3 left-3 right-3 z-[1000]" data-testid="trace-progress">
                      <div className="h-1 bg-zinc-300 dark:bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#00ff9d] to-[#00f0ff] rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(95, (hops.length / 15) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Re-trace button after complete */}
                {traceState === 'complete' && (
                  <div className="mt-4 flex items-center justify-center gap-4">
                    <button
                      data-testid="retrace-button"
                      onClick={() => handleSearch(currentTarget)}
                      className="flex items-center gap-2 px-5 py-2.5 bg-cyan-600/10 dark:bg-[#00f0ff]/10 border border-cyan-600/30 dark:border-[#00f0ff]/30 rounded-xl text-sm font-medium text-cyan-700 dark:text-[#00f0ff] hover:bg-cyan-600/20 dark:hover:bg-[#00f0ff]/20 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Trace Again
                    </button>
                  </div>
                )}

                {/* Error with retry */}
                {error && (
                  <div className="mt-4 px-4 py-3 bg-red-500/10 dark:bg-red-500/10 border border-red-500/20 dark:border-red-500/20 rounded-xl flex items-center justify-between">
                    <span className="text-sm text-red-400 dark:text-red-400">{error}</span>
                    <button
                      data-testid="trace-retry-button"
                      onClick={retryTrace}
                      className="ml-3 px-4 py-1.5 bg-red-500/20 dark:bg-red-500/20 border border-red-500/30 dark:border-red-500/30 rounded-lg text-xs font-semibold text-red-300 dark:text-red-300 hover:bg-red-500/30 dark:hover:bg-red-500/30 transition-colors shrink-0"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>

              {/* Hop Panel */}
              <div className="lg:col-span-5">
                <div className="rounded-2xl overflow-hidden h-full bg-zinc-100 dark:bg-zinc-900/50 border border-zinc-300 dark:border-white/10" style={{ maxHeight: '600px' }}>
                  <HopPanel
                    hops={showTimeoutHops ? allHops : visibleHops}
                    allHops={allHops}
                    traceState={traceState}
                    traceInfo={traceInfo}
                    traceSummary={traceSummary}
                    showTimeoutHops={showTimeoutHops}
                    onToggleTimeoutHops={() => setShowTimeoutHops(!showTimeoutHops)}
                  />
                </div>
              </div>
            </div>

            {/* Bottom features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <div className="flex items-center gap-3 px-4 py-3 bg-zinc-100 dark:bg-white/[0.03] border border-zinc-300 dark:border-white/10 rounded-xl">
                <Zap className="w-8 h-8 text-emerald-600 dark:text-[#00ff9d] shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-zinc-900 dark:text-white/90">Real Traceroute</div>
                  <div className="text-xs text-zinc-600 dark:text-white/50">Actual hop-by-hop data, no simulation</div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-zinc-100 dark:bg-white/[0.03] border border-zinc-300 dark:border-white/10 rounded-xl">
                <Globe className="w-8 h-8 text-cyan-600 dark:text-[#00f0ff] shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-zinc-900 dark:text-white/90">IP Geolocation</div>
                  <div className="text-xs text-zinc-600 dark:text-white/50">Every hop mapped to real coordinates</div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-zinc-100 dark:bg-white/[0.03] border border-zinc-300 dark:border-white/10 rounded-xl">
                <Shield className="w-8 h-8 text-purple-600 dark:text-purple-500 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-zinc-900 dark:text-white/90">Route Protection</div>
                  <div className="text-xs text-zinc-600 dark:text-white/50">Identify vulnerable hops instantly</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-8 border-t border-zinc-300 dark:border-white/10 bg-zinc-100 dark:bg-black/40 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <SignalWavesIcon className="w-4 h-4 text-cyan-600 dark:text-[#00f0ff]" animated={true} />
                <span className="text-sm text-zinc-600 dark:text-white/50">
                  whatismyroute.com &copy; {new Date().getFullYear()}
                </span>
              </div>
            </div>
          </div>
        </footer>
      </main>

      {/* Modals */}
      <LeadModal isOpen={showLeadModal} onClose={() => setShowLeadModal(false)} />
      <TraceHistory isOpen={showHistory} onClose={() => setShowHistory(false)} onReplay={handleReplayTrace} />
    </div>
  );
};

// Admin panel disabled - 404 page
const DisabledPage = () => (
  <div className="min-h-screen bg-white dark:bg-zinc-950 flex items-center justify-center px-4 transition-colors">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-zinc-200 dark:text-white/20 mb-4">404</h1>
      <p className="text-xl text-zinc-700 dark:text-white/70 mb-6">This page has been disabled</p>
      <a href="/" className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600/10 dark:bg-[#00f0ff]/10 border border-cyan-600/30 dark:border-[#00f0ff]/30 rounded-xl text-sm font-medium text-cyan-700 dark:text-[#00f0ff] hover:bg-cyan-600/20 dark:hover:bg-[#00f0ff]/20 transition-colors">
        <SignalWavesIcon className="w-4 h-4" animated={false} />
        Back to Home
      </a>
    </div>
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/stats" element={<Stats />} />
            {/* Admin panel disabled */}
            <Route path="/admin/*" element={<DisabledPage />} />
            <Route path="*" element={<DisabledPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
